import {
    Button,
    Group,
    Modal,
    Paper,
    Stack,
    Text,
    Title,
    Alert,
    Divider,
    ScrollArea,
    Badge,
    Table,
    Radio
} from '@mantine/core';
import {
    AiOutlineWarning,
    AiOutlineCheckCircle,
    AiOutlineClose
} from 'react-icons/ai';
import { useState } from 'react';
import { DuplicateConflict } from '../services/BulkImportService';



export interface DuplicateResolutionModalProps {
    isOpen: boolean;
    conflicts: DuplicateConflict[];
    normalizedDomains?: Map<string, string>; // Show normalization to user
    onResolve: (resolvedConflicts: DuplicateConflict[]) => void;
    onCancel: () => void;
}

export const DuplicateResolutionModal = ({
    isOpen,
    conflicts,
    onResolve,
    onCancel
}: DuplicateResolutionModalProps) => {
    const [resolvedConflicts, setResolvedConflicts] = useState<DuplicateConflict[]>(conflicts);
    const [globalAction, setGlobalAction] = useState<'skip' | 'update' | 'create' | ''>('');

    const handleIndividualActionChange = (index: number, action: 'skip' | 'update' | 'create') => {
        const updated = [...resolvedConflicts];
        updated[index] = { ...updated[index], action };
        setResolvedConflicts(updated);
    };

    const handleGlobalActionChange = (action: 'skip' | 'update' | 'create') => {
        setGlobalAction(action);
        const updated = resolvedConflicts.map(conflict => ({
            ...conflict,
            action
        }));
        setResolvedConflicts(updated);
    };

    const handleResolve = () => {
        onResolve(resolvedConflicts);
    };

    const systemDuplicates = conflicts.filter(c => c.existsInSystem);
    const fileDuplicates = conflicts.filter(c => c.existsInFile && !c.existsInSystem);

    // Helper function to check if domain was normalized
    const isDomainNormalized = (originalDomain: string, normalizedDomain: string): boolean => {
        return originalDomain !== normalizedDomain;
    };

    // Helper function to render domain name with normalization indicator
    const renderDomainName = (conflict: DuplicateConflict) => {
        const isNormalized = isDomainNormalized(conflict.domainName, conflict.normalizedName);
        
        return (
            <Stack gap="xs">
                <Text size="sm" fw={500}>
                    {conflict.domainName}
                </Text>
                {isNormalized && (
                    <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>
                        → {conflict.normalizedName}
                    </Text>
                )}
                {conflict.skipReason && (
                    <Text size="xs" c="orange" style={{ fontStyle: 'italic' }}>
                        {conflict.skipReason}
                    </Text>
                )}
            </Stack>
        );
    };



    return (
        <Modal
            opened={isOpen}
            onClose={onCancel}
            title={
                <Group gap="sm">
                    <AiOutlineWarning color="orange" />
                    <Title order={4}>Resolve Duplicate Domain Names</Title>
                </Group>
            }
            size="xl"
            closeOnClickOutside={false}
            closeOnEscape={true}
            withCloseButton={true}
        >
            <Stack gap="md">
                {/* Summary Alert */}
                <Alert
                    color="orange"
                    variant="light"
                    icon={<AiOutlineWarning />}
                >
                    <Text size="sm" fw={500}>
                        Found {conflicts.length} duplicate domain names that need resolution
                    </Text>
                    <Text size="sm" mt="xs">
                        {systemDuplicates.length} already exist in your account, {fileDuplicates.length} are duplicated within the uploaded file
                    </Text>
                </Alert>

                {/* Global Actions */}
                <Paper p="md" withBorder>
                    <Stack gap="sm">
                        <Text size="sm" fw={500}>Quick Actions:</Text>
                        <Text size="xs" c="dimmed">
                            Apply the same action to all duplicate conflicts at once
                        </Text>
                        <Group gap="md">
                            <Button
                                variant={globalAction === 'skip' ? 'filled' : 'outline'}
                                color="orange"
                                size="sm"
                                onClick={() => handleGlobalActionChange('skip')}
                            >
                                Skip All Duplicates
                            </Button>
                            <Button
                                variant={globalAction === 'update' ? 'filled' : 'outline'}
                                color="blue"
                                size="sm"
                                onClick={() => handleGlobalActionChange('update')}
                                disabled={systemDuplicates.length === 0}
                            >
                                Update Existing Domains
                            </Button>
                            <Button
                                variant={globalAction === 'create' ? 'filled' : 'outline'}
                                color="green"
                                size="sm"
                                onClick={() => handleGlobalActionChange('create')}
                                disabled={fileDuplicates.length === 0}
                            >
                                Create First Occurrences
                            </Button>
                        </Group>
                        <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
                            Remember: Existing domains in your account will never be duplicated
                        </Text>
                    </Stack>
                </Paper>

                {/* System Duplicates */}
                {systemDuplicates.length > 0 && (
                    <Paper p="md" withBorder>
                        <Stack gap="sm">
                            <Text size="sm" fw={500} c="blue">
                                Domains that already exist in your account ({systemDuplicates.length})
                            </Text>
                            <Text size="xs" c="dimmed">
                                These domains already exist in your account. You can skip them or update with new information.
                            </Text>

                            <ScrollArea h={200} type="auto">
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Row</Table.Th>
                                            <Table.Th>Domain Name</Table.Th>
                                            <Table.Th>Status</Table.Th>
                                            <Table.Th>Action</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {systemDuplicates.map((conflict) => {
                                            const globalIndex = resolvedConflicts.findIndex(c => c.domainName === conflict.domainName && c.rowNumber === conflict.rowNumber);
                                            const currentAction = resolvedConflicts[globalIndex]?.action || 'skip';
                                            const willBeSkipped = currentAction === 'skip' || conflict.existsInSystem;
                                            
                                            return (
                                                <Table.Tr key={`${conflict.domainName}-${conflict.rowNumber}`}>
                                                    <Table.Td>
                                                        <Badge variant="light" color="gray" size="sm">
                                                            {conflict.rowNumber}
                                                        </Badge>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        {renderDomainName(conflict)}
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Badge 
                                                            variant="light" 
                                                            color={willBeSkipped ? "orange" : currentAction === 'update' ? "blue" : "green"}
                                                            size="sm"
                                                        >
                                                            {willBeSkipped ? "Will Skip" : currentAction === 'update' ? "Will Update" : "Will Create"}
                                                        </Badge>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Radio.Group
                                                            value={currentAction}
                                                            onChange={(value) => handleIndividualActionChange(globalIndex, value as 'skip' | 'update' | 'create')}
                                                        >
                                                            <Group gap="md">
                                                                <Radio value="skip" label="Skip" color="orange" />
                                                                <Radio value="update" label="Update" color="blue" />
                                                            </Group>
                                                        </Radio.Group>
                                                    </Table.Td>
                                                </Table.Tr>
                                            );
                                        })}
                                    </Table.Tbody>
                                </Table>
                            </ScrollArea>
                        </Stack>
                    </Paper>
                )}

                {/* File Duplicates */}
                {fileDuplicates.length > 0 && (
                    <Paper p="md" withBorder>
                        <Stack gap="sm">
                            <Text size="sm" fw={500} c="green">
                                Duplicate domains within the uploaded file ({fileDuplicates.length})
                            </Text>
                            <Text size="xs" c="dimmed">
                                These domains appear multiple times in your file. You can skip duplicates or create the first occurrence.
                            </Text>

                            <ScrollArea h={200} type="auto">
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Row</Table.Th>
                                            <Table.Th>Domain Name</Table.Th>
                                            <Table.Th>Status</Table.Th>
                                            <Table.Th>Action</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {fileDuplicates.map((conflict) => {
                                            const globalIndex = resolvedConflicts.findIndex(c => c.domainName === conflict.domainName && c.rowNumber === conflict.rowNumber);
                                            const currentAction = resolvedConflicts[globalIndex]?.action || 'skip';
                                            const willBeSkipped = currentAction === 'skip';
                                            
                                            return (
                                                <Table.Tr key={`${conflict.domainName}-${conflict.rowNumber}`}>
                                                    <Table.Td>
                                                        <Badge variant="light" color="gray" size="sm">
                                                            {conflict.rowNumber}
                                                        </Badge>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        {renderDomainName(conflict)}
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Badge 
                                                            variant="light" 
                                                            color={willBeSkipped ? "orange" : "green"}
                                                            size="sm"
                                                        >
                                                            {willBeSkipped ? "Will Skip" : "Will Create"}
                                                        </Badge>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Radio.Group
                                                            value={currentAction}
                                                            onChange={(value) => handleIndividualActionChange(globalIndex, value as 'skip' | 'update' | 'create')}
                                                        >
                                                            <Group gap="md">
                                                                <Radio value="skip" label="Skip" color="orange" />
                                                                <Radio value="create" label="Create First" color="green" />
                                                            </Group>
                                                        </Radio.Group>
                                                    </Table.Td>
                                                </Table.Tr>
                                            );
                                        })}
                                    </Table.Tbody>
                                </Table>
                            </ScrollArea>
                        </Stack>
                    </Paper>
                )}

                {/* Action Summary - Predicted Import Outcomes */}
                <Paper p="md" withBorder bg="gray.0">
                    <Stack gap="sm">
                        <Text size="sm" fw={500}>Predicted Import Outcomes</Text>
                        <Text size="xs" c="dimmed">
                            This shows what will happen when you continue with the import:
                        </Text>
                        <Group gap="md">
                            <Group gap="xs">
                                <Badge color="orange" variant="light">
                                    {resolvedConflicts.filter(c => c.action === 'skip' || c.existsInSystem).length} Will Skip
                                </Badge>
                                <Text size="xs" c="dimmed">
                                    (duplicates and existing domains)
                                </Text>
                            </Group>
                            <Group gap="xs">
                                <Badge color="blue" variant="light">
                                    {resolvedConflicts.filter(c => c.action === 'update' && !c.existsInSystem).length} Will Update
                                </Badge>
                                <Text size="xs" c="dimmed">
                                    (existing domains with new data)
                                </Text>
                            </Group>
                            <Group gap="xs">
                                <Badge color="green" variant="light">
                                    {resolvedConflicts.filter(c => c.action === 'create' && !c.existsInSystem).length} Will Create
                                </Badge>
                                <Text size="xs" c="dimmed">
                                    (new domains only)
                                </Text>
                            </Group>
                        </Group>
                        <Alert color="blue" variant="light">
                            <Text size="xs">
                                Note: Domains that already exist in your account will never be created as duplicates, 
                                regardless of the action selected. They will be skipped or updated as appropriate.
                            </Text>
                        </Alert>
                    </Stack>
                </Paper>

                {/* Action Buttons */}
                <Divider />
                
                <Group justify="space-between">
                    <Button
                        variant="outline"
                        leftSection={<AiOutlineClose />}
                        onClick={onCancel}
                    >
                        Cancel Import
                    </Button>
                    
                    <Button
                        variant="filled"
                        leftSection={<AiOutlineCheckCircle />}
                        onClick={handleResolve}
                        color="blue"
                    >
                        Continue with Selected Actions
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};